import { useEffect, useRef } from 'react'

import { isEqual } from 'lodash'

import settings from 'app/settings'
import useLDFlags from 'data/hooks/useLDFlags'
import { fetchWithAuth } from 'data/utils/fetchWithAuth'
import { useCanAccessSupport } from 'utils/intercom'

import { useAppContext } from './AppContext'
import { useAppUserContext } from './AppUserContext'

const PYLON_TOKEN_URL = 'pylon-token/'

type ChatSettings = {
    app_id: string
    email?: string
    name?: string
    avatar_url?: string
    user_hash?: string
}

type BootPylonProps = {
    user?: any
    account?: any
    callback?: any
    extraSettings?: Record<string, any>
    stackId?: string
}

type PylonSettings = {
    email: string
    name: string
    avatar_url: any
    account_id?: string
}

export const _bootPylon = (pylonSettings: ChatSettings, callback?: (pylon: any) => void) => {
    try {
        if (window.Pylon) {
            window.pylon = { chat_settings: pylonSettings }

            const customFields = {
                workspace_type_2: 'classic',
                // see https://docs.usepylon.com/pylon-docs/in-app-chat/javascript-api#window-pylon-setnewissuecustomfields
                // 👇 Add any additional custom fields here
            }

            //@ts-ignore
            window.Pylon('setNewIssueCustomFields', customFields)

            if (callback) callback(window.Pylon)
        } else {
            // if Pylon not yet available, retry every 3s until it is
            setTimeout(() => {
                _bootPylon(pylonSettings, callback)
            }, 3000)
        }
    } catch (err) {
        console.error('Unable to boot Pylon', err)
    }
}

export const bootPylon = async ({
    user = undefined,
    account = undefined,
    callback = undefined,
    extraSettings = {},
}: BootPylonProps) => {
    let userHash: string | undefined

    if (!user || !user?.membership_options || user?.membership_options?.role === 'guest') return

    if (user.email) {
        try {
            const response = await fetchWithAuth(PYLON_TOKEN_URL)
            const data = await response.json()
            userHash = data.token
        } catch (err) {
            console.error('Failed to fetch Pylon user hash:', err)
        }
    }

    const chatSettings = {
        app_id: settings.PYLON_APP_ID,
        email: user.email,
        name: user.name,
        avatar_url: user.avatar,
        ...extraSettings,
        ...(userHash ? { email_hash: userHash } : {}),
        ...(account ? { account_id: account._sid } : {}),
    }

    _bootPylon(chatSettings, callback)
}

declare global {
    // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
    interface Window {
        Pylon: unknown
        pylon: unknown
    }
}

export const PylonIntegration: React.FC = () => {
    const { user } = useAppUserContext()
    const { workspaceAccount } = useAppContext()
    const canAccessSupport = useCanAccessSupport()
    const currentPylonSettings = useRef<PylonSettings | undefined>()
    const { flags } = useLDFlags()

    const currentUser = user

    useEffect(() => {
        if (!currentUser || !canAccessSupport || flags.cohereChimeEnabled) return

        // Only boot Pylon if user has support access
        const pylonSettings = {
            email: currentUser.email,
            name: currentUser.name || currentUser.email,
            avatar_url: currentUser.avatar,
            ...(workspaceAccount ? { account_id: workspaceAccount._sid } : {}),
        }

        // Only reboot if settings changed
        if (!isEqual(pylonSettings, currentPylonSettings.current)) {
            currentPylonSettings.current = pylonSettings
            bootPylon({
                user: currentUser,
                account: workspaceAccount,
            })
        }
    }, [currentUser, canAccessSupport, flags.cohereChimeEnabled, workspaceAccount])

    return null
}

/*
 * Helper that other components can import to open the chat programmatically.
 * We colocate it here to keep all Pylon-specific logic in one module.
 */
export const openChat = () => {
    try {
        // Only attempt if the Pylon JS snippet has loaded.
        //@ts-ignore – runtime guard.
        if (window.Pylon) {
            //@ts-ignore
            window.Pylon('show')
        }
    } catch (err) {
        console.error('Failed to open Pylon chat:', err)
    }
}
